import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Trash2, Plus, Calculator, ShieldAlert, ShieldCheck, HelpCircle, ChevronDown } from 'lucide-react';
import PageShell, { SectionTitle } from './PageShell';

/* ══════════ Données boissons ══════════
 * Volume en mL, degré en % vol. Alcool pur (g) = volume × degré × 0.8 / 100
 */
const DRINKS = {
  'biere':        { label: 'Bière (25 cl · 5°)',      vol: 250, deg: 5,   emoji: '🍺' },
  'biere-forte':  { label: 'Bière forte (33 cl · 8°)', vol: 330, deg: 8,   emoji: '🍺' },
  'vin':          { label: 'Vin (12,5 cl · 12°)',      vol: 125, deg: 12,  emoji: '🍷' },
  'champagne':    { label: 'Champagne (10 cl · 12°)',  vol: 100, deg: 12,  emoji: '🥂' },
  'spiritueux':   { label: 'Spiritueux (3 cl · 40°)',  vol: 30,  deg: 40,  emoji: '🥃' },
  'whisky':       { label: 'Whisky (4 cl · 40°)',      vol: 40,  deg: 40,  emoji: '🥃' },
  'cocktail':     { label: 'Cocktail (15 cl · 12°)',   vol: 150, deg: 12,  emoji: '🍸' },
  'perso':        { label: 'Personnalisé',              vol: 100, deg: 10, emoji: '🍹' },
};

const ELIM_RATE = 0.15; // g/L par heure (moyenne — plage 0.10 à 0.15)
const DIFFUSION = { M: 0.68, F: 0.55 };

function parseTime(hhmm) {
  const [h, m] = (hhmm || '00:00').split(':').map((n) => parseInt(n, 10) || 0);
  return h + m / 60;
}
function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Calcul de l'alcoolémie à un instant t.
 * Chaque boisson contribue individuellement selon son heure de consommation.
 * Formule de Widmark : C = alcool (g) / (poids × diffusion) - β × t
 */
function computeBAC({ drinks, weight, sex, currentHour }) {
  const beta = ELIM_RATE;
  const diff = DIFFUSION[sex] || DIFFUSION.M;
  let total = 0;
  const perDrink = drinks.map((d) => {
    const alcoolPur = (d.vol * d.deg * 0.8) / 100; // grammes
    const hoursSince = Math.max(0, currentHour - parseTime(d.time));
    const initialBAC = alcoolPur / (weight * diff); // g/L
    // Modèle simplifié : pic atteint immédiatement puis élimination linéaire
    const remaining = Math.max(0, initialBAC - beta * hoursSince);
    total += remaining;
    return { ...d, alcoolPur, initialBAC, remaining, hoursSince };
  });
  return { totalBAC: total, perDrink };
}

function timeUntilBelow(bac, target) {
  if (bac <= target) return 0;
  return (bac - target) / ELIM_RATE; // heures
}
function fmtDuration(h) {
  if (h <= 0) return "Vous êtes en dessous du seuil";
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}h${String(mm).padStart(2, '0')}`;
}

/* ══════════ Composant ══════════ */
export default function SimulateurAlcoolPage() {
  const [sex, setSex] = useState('M');
  const [weight, setWeight] = useState(75);
  const [currentTime, setCurrentTime] = useState(nowHHMM());
  const [permitType, setPermitType] = useState('normal'); // normal | probatoire
  const [drinks, setDrinks] = useState([
    { id: 1, type: 'biere', time: '20:00', vol: 250, deg: 5 },
  ]);
  const [showFaq, setShowFaq] = useState(false);

  const addDrink = () => {
    const id = Date.now();
    setDrinks((ds) => [...ds, { id, type: 'biere', time: currentTime, vol: 250, deg: 5 }]);
  };
  const removeDrink = (id) => setDrinks((ds) => ds.filter((d) => d.id !== id));
  const updateDrink = (id, patch) => setDrinks((ds) => ds.map((d) => d.id === id ? { ...d, ...patch } : d));
  const changeType = (id, type) => {
    const preset = DRINKS[type] || DRINKS.perso;
    updateDrink(id, { type, vol: preset.vol, deg: preset.deg });
  };

  const legalLimit = permitType === 'probatoire' ? 0.2 : 0.5;
  const result = useMemo(() => computeBAC({
    drinks, weight, sex, currentHour: parseTime(currentTime),
  }), [drinks, weight, sex, currentTime]);
  const bac = result.totalBAC;
  const status = bac === 0 ? 'safe' : bac < legalLimit ? 'below' : bac < 0.8 ? 'infraction' : 'delit';
  const timeUntilLegal = timeUntilBelow(bac, legalLimit);
  const timeUntilZero = timeUntilBelow(bac, 0);

  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 py-10 sm:py-14" data-testid="simulateur-alcool">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Simulateur d&apos;alcoolémie</span>
        </nav>

        <SectionTitle
          kicker="Formule de Widmark · Sécurité routière"
          title="Simulateur d'alcoolémie"
        />
        <p className="mt-5 text-white/75 text-[15px] max-w-3xl leading-relaxed">
          Calcule ton taux d&apos;alcool dans le sang à partir de tes consommations réelles, ton poids et ton sexe.
          <strong className="text-white"> Élimination : 0,15 g/L par heure en moyenne — un rythme incompressible.</strong>
        </p>

        {/* Warning */}
        <div className="mt-6 rounded-xl border border-amber-500/40 bg-amber-950/20 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-300 shrink-0 mt-0.5" />
          <p className="text-white/85 text-[13px] leading-relaxed">
            <strong className="text-amber-200">Estimation théorique uniquement.</strong> Le taux réel dépend du métabolisme, de l&apos;alimentation, de la fatigue, des médicaments, de l&apos;âge. En cas de doute : ne prends jamais le volant. Ce simulateur ne remplace pas un éthylotest certifié.
          </p>
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* ── Colonne 1 : Paramètres personnels ── */}
          <div className="lg:col-span-1 space-y-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-4">📊 Vos informations</div>

              <label className="block text-white/60 text-[11.5px] font-mono-spec uppercase tracking-wider mb-1.5">Sexe</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <button data-testid="sex-M" onClick={() => setSex('M')} className={`h-11 rounded-full border text-[13px] font-semibold transition ${sex === 'M' ? 'border-red-500/60 bg-red-500/15 text-red-100' : 'border-white/15 text-white/60 hover:text-white'}`}>Homme</button>
                <button data-testid="sex-F" onClick={() => setSex('F')} className={`h-11 rounded-full border text-[13px] font-semibold transition ${sex === 'F' ? 'border-red-500/60 bg-red-500/15 text-red-100' : 'border-white/15 text-white/60 hover:text-white'}`}>Femme</button>
              </div>

              <label className="block text-white/60 text-[11.5px] font-mono-spec uppercase tracking-wider mb-1.5">Poids : <span className="text-white font-bold">{weight} kg</span></label>
              <input data-testid="weight-input" type="range" min="35" max="150" value={weight} onChange={(e) => setWeight(parseInt(e.target.value, 10))} className="w-full accent-red-500 mb-4" />

              <label className="block text-white/60 text-[11.5px] font-mono-spec uppercase tracking-wider mb-1.5">Heure actuelle</label>
              <input data-testid="current-time" type="time" value={currentTime} onChange={(e) => setCurrentTime(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-white/15 bg-black/40 text-white text-[13.5px] mb-4" />

              <label className="block text-white/60 text-[11.5px] font-mono-spec uppercase tracking-wider mb-1.5">Type de permis</label>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="permit-normal" onClick={() => setPermitType('normal')} className={`h-11 rounded-full border text-[12px] font-semibold transition ${permitType === 'normal' ? 'border-white/50 bg-white/10 text-white' : 'border-white/15 text-white/60'}`}>Normal · 0,5</button>
                <button data-testid="permit-probatoire" onClick={() => setPermitType('probatoire')} className={`h-11 rounded-full border text-[12px] font-semibold transition ${permitType === 'probatoire' ? 'border-red-500/60 bg-red-500/15 text-red-100' : 'border-white/15 text-white/60'}`}>Probatoire · 0,2</button>
              </div>
            </div>

            {/* Limites légales */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-3">⚖️ Limites légales · France</div>
              <div className="space-y-2 text-[13px]">
                <div className="flex justify-between text-white/80"><span>Permis normal (sang)</span><span className="font-mono-spec text-white">0,5 g/L</span></div>
                <div className="flex justify-between text-white/80"><span>Permis probatoire (sang)</span><span className="font-mono-spec text-red-300">0,2 g/L</span></div>
                <div className="flex justify-between text-white/80"><span>Air expiré (normal)</span><span className="font-mono-spec text-white">0,25 mg/L</span></div>
                <div className="flex justify-between text-white/80"><span>Air expiré (probatoire)</span><span className="font-mono-spec text-red-300">0,10 mg/L</span></div>
              </div>
              <p className="mt-4 text-white/60 text-[12px] leading-relaxed">
                Au-delà de <strong className="text-red-300">0,8 g/L</strong> : délit passible de 2 ans d&apos;emprisonnement, 4 500 € d&apos;amende, retrait 6 points, suspension.
              </p>
            </div>
          </div>

          {/* ── Colonne 2-3 : Boissons + Résultat ── */}
          <div className="lg:col-span-2 space-y-5">
            {/* Résultat */}
            <div className={`rounded-2xl border p-6 ${status === 'safe' || status === 'below' ? 'border-emerald-500/40 bg-emerald-950/20' : status === 'infraction' ? 'border-amber-500/50 bg-amber-950/25' : 'border-red-500/60 bg-red-950/30'}`} data-testid="bac-result">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className={`text-[10.5px] font-mono-spec uppercase tracking-wider font-bold ${status === 'safe' || status === 'below' ? 'text-emerald-300' : status === 'infraction' ? 'text-amber-300' : 'text-red-300'}`}>
                    {status === 'safe' && '✅ Aucun alcool détecté'}
                    {status === 'below' && '✅ Sous le seuil légal'}
                    {status === 'infraction' && '⚠️ Infraction · Amende + retrait de points'}
                    {status === 'delit' && '🚨 DÉLIT · Sanctions pénales'}
                  </div>
                  <div className="mt-2 font-serif-editorial text-white text-[52px] leading-none" data-testid="bac-value">{bac.toFixed(2)} g/L</div>
                  <div className="mt-1 text-white/50 text-[12.5px] font-mono-spec">Seuil applicable : {legalLimit} g/L ({permitType})</div>
                </div>
                {(status === 'safe' || status === 'below') ? <ShieldCheck size={44} className="text-emerald-400 shrink-0" strokeWidth={1.3} /> : <ShieldAlert size={44} className="text-red-400 shrink-0" strokeWidth={1.3} />}
              </div>
              {bac > 0 && (
                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-white/10 bg-black/30 p-3">
                    <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider">Temps avant seuil légal ({legalLimit} g/L)</div>
                    <div className="mt-1.5 font-serif-editorial text-white text-[22px]">{fmtDuration(timeUntilLegal)}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-black/30 p-3">
                    <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider">Temps avant 0 g/L</div>
                    <div className="mt-1.5 font-serif-editorial text-white text-[22px]">{fmtDuration(timeUntilZero)}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Boissons */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">🍺 Boissons consommées</div>
                <button onClick={addDrink} data-testid="add-drink" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold transition">
                  <Plus size={13} /> Ajouter
                </button>
              </div>

              <div className="space-y-3">
                {drinks.map((d) => {
                  const preset = DRINKS[d.type] || DRINKS.perso;
                  const per = result.perDrink.find((p) => p.id === d.id);
                  return (
                    <div key={d.id} className="rounded-xl border border-white/10 bg-black/25 p-3.5" data-testid={`drink-${d.id}`}>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-12 sm:col-span-5">
                          <label className="block text-white/50 text-[10px] font-mono-spec uppercase tracking-wider mb-1">Boisson</label>
                          <select value={d.type} onChange={(e) => changeType(d.id, e.target.value)} className="w-full h-10 px-2 rounded-lg border border-white/15 bg-black/50 text-white text-[12.5px]">
                            {Object.entries(DRINKS).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
                          </select>
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <label className="block text-white/50 text-[10px] font-mono-spec uppercase tracking-wider mb-1">Heure</label>
                          <input type="time" value={d.time} onChange={(e) => updateDrink(d.id, { time: e.target.value })} className="w-full h-10 px-2 rounded-lg border border-white/15 bg-black/50 text-white text-[12.5px]" />
                        </div>
                        <div className="col-span-3 sm:col-span-2">
                          <label className="block text-white/50 text-[10px] font-mono-spec uppercase tracking-wider mb-1">Vol (mL)</label>
                          <input type="number" value={d.vol} min="10" max="1000" onChange={(e) => updateDrink(d.id, { vol: parseInt(e.target.value, 10) || 0 })} disabled={d.type !== 'perso'} className="w-full h-10 px-2 rounded-lg border border-white/15 bg-black/50 text-white text-[12.5px] disabled:opacity-50" />
                        </div>
                        <div className="col-span-3 sm:col-span-2">
                          <label className="block text-white/50 text-[10px] font-mono-spec uppercase tracking-wider mb-1">Degré</label>
                          <input type="number" value={d.deg} min="0" max="80" step="0.5" onChange={(e) => updateDrink(d.id, { deg: parseFloat(e.target.value) || 0 })} disabled={d.type !== 'perso'} className="w-full h-10 px-2 rounded-lg border border-white/15 bg-black/50 text-white text-[12.5px] disabled:opacity-50" />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <button onClick={() => removeDrink(d.id)} data-testid={`remove-drink-${d.id}`} className="w-full h-10 rounded-lg border border-red-500/30 bg-red-950/20 text-red-300 hover:bg-red-950/40 flex items-center justify-center transition">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      {per && (
                        <div className="mt-2.5 text-[11.5px] text-white/60 font-mono-spec flex flex-wrap gap-x-4 gap-y-1">
                          <span>{preset.emoji} {per.alcoolPur.toFixed(1)} g d&apos;alcool pur</span>
                          <span>· Pic à {per.initialBAC.toFixed(2)} g/L</span>
                          <span>· <span className={per.remaining > 0 ? 'text-red-300' : 'text-emerald-300'}>Reste {per.remaining.toFixed(2)} g/L</span></span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {drinks.length === 0 && <div className="text-white/40 text-[13px] text-center py-6">Aucune boisson ajoutée</div>}
              </div>
            </div>

            {/* Conseils sécurité */}
            <div className="rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-950/25 via-[#0d0d0e] to-[#0a0a0b] p-5">
              <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-3">🚨 Conseils de sécurité</div>
              <ul className="space-y-1.5 text-white/80 text-[13px]">
                <li>▸ En cas de doute, <strong className="text-white">ne prends pas le volant</strong></li>
                <li>▸ Désigne un <strong className="text-white">SAM · Sans Accident Mortel</strong> pour la soirée</li>
                <li>▸ Utilise transports en commun, taxi, VTC — ou dors sur place</li>
                <li>▸ Le <strong className="text-red-300">lendemain matin</strong>, tu peux encore être au-dessus de la limite</li>
                <li>▸ Café · douche · sport = <strong className="text-red-300">INEFFICACES</strong> pour éliminer l&apos;alcool. Seul le temps compte.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <section className="mt-14">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <HelpCircle size={11} /> Questions fréquentes
          </div>
          <button onClick={() => setShowFaq((s) => !s)} className="mt-3 flex items-center gap-2 text-white text-[16px] font-semibold hover:text-red-300 transition">
            Comment est calculé le taux d&apos;alcoolémie ? <ChevronDown size={16} className={`transition-transform ${showFaq ? 'rotate-180' : ''}`} />
          </button>
          {showFaq && (
            <div className="mt-4 space-y-4 text-white/75 text-[13.5px] leading-relaxed max-w-3xl">
              <div><strong className="text-white">Formule de Widmark :</strong> Taux (g/L) = alcool pur (g) ÷ (poids (kg) × coefficient de diffusion) − 0,15 × temps écoulé (h). Coefficient : 0,68 pour ♂ · 0,55 pour ♀ (reflète la proportion d&apos;eau corporelle).</div>
              <div><strong className="text-white">Pourquoi l&apos;heure de chaque boisson ?</strong> L&apos;alcool est éliminé dès sa consommation. Une bière prise à 20h et une autre à 23h n&apos;auront pas le même impact à minuit : la première sera déjà partiellement éliminée. Ce simulateur calcule chaque contribution individuellement.</div>
              <div><strong className="text-white">Combien de temps pour éliminer 1 verre standard ?</strong> Environ 1h30 à 2h en moyenne. Ce rythme ne peut PAS être accéléré (café, douche froide, exercice = inefficaces). Seul le temps permet d&apos;éliminer l&apos;alcool.</div>
              <div><strong className="text-white">Fiabilité à 100 % ?</strong> Non. Le taux réel varie selon : métabolisme, état de santé, alimentation (estomac vide/plein), fatigue, médicaments, âge. Toujours vérifier avec un éthylotest certifié avant de conduire.</div>
              <div><strong className="text-white">Sanctions en cas de contrôle positif ?</strong> Entre 0,5 et 0,8 g/L : amende 135 € + retrait 6 points. Au-delà de 0,8 g/L : délit pénal — 2 ans d&apos;emprisonnement, 4 500 € d&apos;amende, suspension jusqu&apos;à 3 ans, stage obligatoire, immobilisation du véhicule.</div>
            </div>
          )}
        </section>

        {/* CTA vers RVP */}
        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Calculator size={28} className="text-white/40 shrink-0" strokeWidth={1.4} />
          <div className="flex-1">
            <div className="font-serif-editorial text-white text-[18px]">Prolonger la réflexion</div>
            <p className="text-white/60 text-[13px] mt-1">Retrouve les fondamentaux de la conduite accompagnée + les 4 grandes compétences REMC.</p>
          </div>
          <Link to="/formation/conduite-accompagnee-aac" className="shrink-0 inline-flex items-center gap-2 h-11 px-5 rounded-full border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 text-[13px] font-semibold transition">
            Voir la fiche AAC complète →
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
